<template>
    <div class="watchlist-selection">
        <div class="watchlist-selection__scrollbar -empty" v-if="watchlists.length === 0"></div>
        <TotemScrollbar class="watchlist-selection__scrollbar" v-if="watchlists.length > 0">
            <ul class="watchlist-selection__list">
                <li
                    v-for="(item, index) in computedWatchlist"
                    class="watchlist-selection__list-item"
                    :class="{'-active': selectedWatchList && selectedWatchList.id === item.id, '-hidden': isHiddenWatchList(item) }"
                    :key="index"
                    @click="selectPlaylist(item)"
                >
                    <span class="watchlist-selection__title">{{ item.title }}</span>
                    <div class="watchlist-selection__statuses" v-if="isDesktopView">
                        <div
                            v-for="status in getStatuses(item)"
                            :key="status.key"
                            class="watchlist-selection__status"
                        >
                            <span :class="status.class"></span>
                            <span class="watchlist-selection__status-text">{{ $t(status.text) }}</span>
                        </div>

                        <div class="watchlist-selection__share-state" v-if="item.mode !== null && isReportingAllowed">
                            <span>
                                <template v-if="isLoadingSubordinates">
                                    <div class="spinner icon-spinner"></div>
                                </template>
                                <template v-else>
                                    <span v-html="$t('WatchListSelection.shared_for')"></span>
                                    {{ getSharedAmount(item) }}
                                    <span v-html="$t('WatchListSelection.users')"></span>
                                </template>
                            </span>
                        </div>
                        <div class="watchlist-selection__share-state" v-if="item.mode === null && isReportingAllowed && !item.isReadonly">
                            <span v-html="$t('WatchListSelection.not_shared')"></span>
                        </div>
                    </div>
                </li>
                <li
                    v-if="isEveryWatchListHidden && !showHiddenWatchLists"
                    class="watchlist-selection__all-hidden"
                >
                    <span class="watchlist-selection__all-hidden-notification">
                        <span v-html="$t('WatchListSelection.all_hidden_notification')"></span>
                        <span class="watchlist-selection__status-hidden icon-eye-blocked"></span>
                    </span>
                    <span class="watchlist-selection__all-hidden-instruction" v-html="$t('WatchListSelection.all_hidden_instruction')"></span>
                </li>
            </ul>
        </TotemScrollbar>
        <div class="watchlist-selection__options">
            <TotemToast ref="toast "/>
            <div class="watchlist-selection__buttons">
                <TotemToggleButton
                    v-model="showHiddenWatchLists"
                    :value="showHiddenWatchLists"
                    :label="$t('WatchListSelection.show_hidden_watchlists')"
                    class="watchlist-selection__toggle-button"
                />
                <TotemButton
                    :class="['watchlist-button', {'-restricted-buttons': isSelectedWatchlistReadonly}]"
                    color="primary"
                    @click="createNewWatchlist"
                    :label="$t('WatchListSelection.new_watchlist')"
                />
                <TotemButton
                    class="watchlist-button"
                    v-if="!isSelectedWatchlistReadonly"
                    color="secondary"
                    @click="removeWatchlist"
                    :disabled="!selectedWatchList"
                    :label="$t('WatchListSelection.remove_watchlist')"
                />
                <template v-if="isSelectedWatchlistShareable">
                    <TotemButton
                        class="watchlist-button"
                        color="secondary"
                        :disabled="isLoadingSubordinates"
                        :label="$t('WatchListSelection.please_wait')"
                        icon="spinner"
                        v-if="isLoadingSubordinates"
                    />
                    <TotemButton
                        class="watchlist-button"
                        color="secondary"
                        @click="shareWatchList"
                        :disabled="isLoadingSubordinates"
                        :label="$t('WatchListSelection.share_watchlist')"
                        v-else
                    />
                </template>
                <template v-if="selectedWatchList && !selectedWatchList.isReadonly">
                    <TotemButton
                            v-if="!selectedWatchList.isHidden"
                            color="secondary"
                            class="watchlist-button"
                            @click="hideOrShowWatchList"
                            :label="$t('WatchListSelection.hide_watchlist')"
                    />
                    <TotemButton
                            v-if="selectedWatchList.isHidden"
                            color="secondary"
                            class="watchlist-button"
                            @click="hideOrShowWatchList"
                            :label="$t('WatchListSelection.show_watchlist')"
                    />
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'

    export default {
        props: {
            watchlists: Array,
            value: Boolean
        },
        data () {
            return {
                assignedOnStart: false,
                selectedWatchList: null,
                showHiddenWatchLists: true
            }
        },
        computed: {
            ...mapGetters({
                areThereCourses: 'Courses/areThereCourses',
                isReportingAllowed: 'User/getIsReportingAllowed',
                getSelectedWatchList: 'Watchlists/getSelectedWatchList',
                selectedSubordinates: 'Watchlists/getShareWatchListSelectedSubordinates',
                selectedMembers: 'Watchlists/getShareWatchListSelectedMembers',
                subordinates: 'Watchlists/getSubordinates',
                isLoadingSubordinates: 'Watchlists/getIsLoadingSubordinates'
            }),
            isSelectedWatchlistShareable () {
                return this.isReportingAllowed && this.selectedWatchList && !this.isSelectedWatchlistReadonly
            },
            isSelectedWatchlistAlreadyShared () {
                return this.selectedWatchList && this.selectedWatchList.isShared
            },
            isSelectedWatchlistReadonly () {
                return this.selectedWatchList && this.selectedWatchList.isReadonly
            },
            computedWatchlist () {
                return this.watchlists.filter((item) => this.checkHiddenWatchLists(item))
            },
            isEveryWatchListHidden () {
                return this.watchlists.every(watchlist => watchlist.isHidden)
            }
        },
        methods: {
            getStatuses (item) {
                const statuses = []
                if (item.isHidden) {
                    statuses.push({
                        key: 'hidden',
                        class: 'watchlist-selection__status-hidden icon-eye-blocked',
                        text: 'Watchlist.infobox.hidden',
                    })
                }

                if (item.isReadonly) {
                    statuses.push({
                        key: 'readonly',
                        class: 'icon-user watchlist-selection__status',
                        text: 'Watchlist.infobox.readonly',
                    })
                }

                if (item.mode !== null && item.isShared && !item.isReadonly) {
                    statuses.push({
                        key: 'shared',
                        class: 'icon-share watchlist-selection__status',
                        text: 'Watchlist.infobox.shared',
                    })
                }

                if (item.mode === null && !item.isReadonly) {
                    statuses.push({
                        key: 'normal',
                        class: 'icon-user watchlist-selection__status',
                        text: 'Watchlist.infobox.normal',
                    })
                }
                return statuses
            },
            unselectWatchlist () {
                this.selectedWatchList = null
            },
            onWatchlistCreated () {
                this.showToast(this.$t('WatchListSelection.study_plan_created'))
            },
            onWatchlistRemoved () {
                this.showToast(this.$t('WatchListSelection.study_plan_removed'))
            },
            onWatchlistShared () {
                this.showToast(this.$t('WatchListSelection.study_plan_shared'))
            },
            onWatchlistSharingRemovedFromUsers (amountOfUsers) {
                this.showToast(this.$t('WatchListSelection.study_plan_sharing_removed_from_users', { subordinates: amountOfUsers }))
            },
            onWatchlistSharedToAmountOfUsers () {
                const textStart = this.$t('WatchListModal.share_watchlist_shared_with_users_start')
                const textEnd = this.$t('WatchListModal.share_watchlist_shared_with_users_end')
                const toastText = textStart + ' ' + this.getSelectedWatchList.users.length + ' ' + textEnd
                this.showToast(toastText)
            },
            onUpdateSharedWatchListUpdated () {
                this.showToast(this.$t('WatchListSelection.shared_study_plan_updated'))
            },
            removeWatchlist (id) {
                this.$emit('removeWatchlist')
            },
            createNewWatchlist () {
                this.$emit('createNewWatchlist')
            },
            shareWatchList () {
                this.$emit('openShareWatchListMenu')
            },
            updateWatchList () {
                this.$emit('updateSharedWatchList')
            },
            hideOrShowWatchList () {
                this.$emit('hideOrShowWatchList')
            },
            selectPlaylist (item) {
                this.selectedWatchList = {
                    id: item.id,
                    isShared: item.isShared,
                    isReadonly: item.isReadonly,
                    isHidden: item.isHidden
                }
                this.$store.dispatch('Watchlists/assignSelectedWatchList', item)
                this.$emit('selectWatchlist', item.id)
            },
            showToast (text) {
                this.$refs.toast.show(text)
            },
            toggleHiddenWatchlist () {
                this.editedWatchList.isHidden = !this.editedWatchList.isHidden
            },
            checkHiddenWatchLists (watchlist) {
                if (this.showHiddenWatchLists) {
                    return true
                }
                return !watchlist.isHidden
            },
            isHiddenWatchList (watchlist) {
                return watchlist.isHidden
            },
            getSharedAmount (watchlist) {
                switch (watchlist.mode) {
                case 0:
                    if (this.subordinates === null) {
                        return ' '
                    }
                    return ' ' + this.subordinates.length + ' '
                case 1:
                    return ' ' + watchlist.users.length + ' '
                }
            }
        },
        watch: {
            watchlists: {
                handler (val) {
                    if (!this.assignedOnStart) {
                        if (val.length > 0) {
                            if (this.getSelectedWatchList === null) {
                                this.selectPlaylist(val.find(watchlist => !watchlist.isHidden))
                            } else {
                                this.selectPlaylist(this.getSelectedWatchList)
                            }
                            this.assignedOnStart = true
                        }
                    }
                },
                immediate: true
            }
        }
    }
</script>

<style lang="scss" scoped>
    .watchlist-selection {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        background: $concrete;
        padding: 30px 20px;
        margin: 0 0 20px;

        .icon-spinner {
            margin: 0 5px;
        }

        [class*="mobile"] & {
            padding: 0;
        }

        &__scrollbar {
            font-family: PlusJakartaSans-Medium, sans-serif;
            flex-grow: 1;
            border: 1px solid $dove_gray;
            background: $white;
            border-radius: 6px;

            &.-empty {
                height: 154px;
            }

            [class*="desktop"] & {
                margin: 0 20px 0 0;
            }
        }

        &__list {
            height: 240px;
        }

        &__title {
            flex: 0 0 50%;

            [class*="desktop_l"] & {
                flex: 0 0 30%;
            }

            [class*="desktop_xl"] & {
                flex: 0 0 40%;
            }
        }

        &__statuses {
            flex: 0 0 50%;
            display: flex;

            [class*="desktop_l"] & {
                flex: 0 0 70%;
            }

            [class*="desktop_xl"] & {
                flex: 0 0 60%;
            }
        }

        &__status {
            border-radius: 100%;
            font-size: 1.6rem;
            margin: 0 10px 0 0;
        }

        &__status-hidden {
            margin-right: 8px;
        }

        &__list-item {
            display: flex;
            padding: 14px 20px;
            flex: 1 0 0;
            align-self: stretch;

            [class*="desktop"] & {
                align-items: center;
                gap: 7px;
            }

            [class*="mobile"] & {
                font-size: 1.4rem;
                padding: 14px;
            }

            &:hover {
                cursor: pointer;
                background-color: $gallery;
            }

            &.-active {
                background-color: $light_blue;
            }
            &.-hidden {
                opacity: 0.6;
            }
        }

        &__share-state {
            text-transform: lowercase;
            position: relative;
            margin-left: 10px;

            &:before {
                content: '-';
                display: inline-block;
                position: absolute;
                left: -14px;
            }
        }

        &__all-hidden {
            padding: 30px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-direction: column;
        }
        &__all-hidden-notification {
            font-size: 1.8rem;
            line-height: 3.2rem;
            opacity: 0.6;
        }
        &__all-hidden-instruction {
            font-size: 1.3rem;
            line-height: 3.2rem;
            opacity: 0.6;
        }
        &__select {
            margin: 0 30px 20px 0;
        }

        &__buttons {
            display: flex;
            gap: 16px;
            justify-content: flex-start;
            align-items: center;
            flex-wrap: wrap;

            [class*="mobile"] & {
                margin: 0 0 0 10px;
            }

            .watchlist-button {
                min-width: 120px;
                max-height: 40px;
                text-align: center;
                white-space: nowrap;

                &.-restricted-buttons {
                    flex: 0 1 calc(50% - 5px);
                    height: 40px;
                }
            }
        }

        &__toggle-info {
            [class*="desktop"] & {
                margin: 0 15px 0 0;
            }
            [class*="mobile"] & {
                margin: 0 auto;
            }
        }

        &__options {
            display: flex;
            flex-direction: column;
            width: 520px;
            gap: 16px;
            padding: 0 70px 0 0;

            [class*="desktop"] & {
                flex-direction: row;
                margin: 20px 0 0;
                justify-content: space-between;
                width: 100%;
            }

            [class*="mobile"] & {
                margin: 20px 0;
                justify-content: space-between;
                width: 100%;
            }
        }
    }
</style>
